// src/components/ArticleSchemaManager.jsx
import React, { useState, useEffect } from 'react';
import { Database, Check, AlertTriangle, RefreshCw, X, Table, PlusCircle } from 'lucide-react';

const ArticleSchemaManager = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [schemaInfo, setSchemaInfo] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);

  // Load schema info on component mount
  useEffect(() => {
    fetchSchemaInfo();
  }, []);

  const fetchSchemaInfo = async () => {
    setLoadingSchema(true);
    
    try {
      const response = await fetch('/api/db-schema');
      const data = await response.json();
      
      setSchemaInfo(data);
    } catch (err) {
      console.error('Error fetching schema info:', err);
    } finally {
      setLoadingSchema(false);
    }
  };

  const handleCreateTables = async (endpoint) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      setResult(data);
      
      // Refresh schema info after creating tables
      await fetchSchemaInfo();
    } catch (err) {
      console.error('Error creating article tables:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Check if article_claims table exists
  const articleClaimsExists = schemaInfo?.tables?.includes('article_claims');
  
  // Check if analyzed_articles table exists
  const analyzedArticlesExists = schemaInfo?.tables?.includes('analyzed_articles');

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
        <Database className="mr-2 text-blue-500" size={20} />
        Article Schema Manager
      </h2>
      
      <p className="text-gray-600 mb-4">
        Use this tool to ensure the database has all required tables for article analysis.
        This creates the necessary tables for storing article analysis results, including
        connections to democratic pillars, authoritarian tactics, precedents, and more.
      </p>

      {/* Schema Status */}
      <div className="mb-6 border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-lg mb-2 flex items-center">
          <Table className="mr-2 text-gray-500" size={18} />
          Current Article Schema Status
        </h3>
        
        {loadingSchema ? (
          <div className="flex items-center text-gray-500">
            <RefreshCw className="animate-spin mr-2" size={16} />
            Loading schema information...
          </div>
        ) : schemaInfo ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-1">Core Tables:</h4>
              <ul className="list-disc ml-5">
                <li className={analyzedArticlesExists ? "text-green-600" : "text-red-600"}>
                  analyzed_articles {analyzedArticlesExists ? "✓" : "✗"}
                </li>
                <li className={schemaInfo.tables.includes('article_content') ? "text-green-600" : "text-red-600"}>
                  article_content {schemaInfo.tables.includes('article_content') ? "✓" : "✗"}
                </li>
                <li className={articleClaimsExists ? "text-green-600" : "text-red-600"}>
                  article_claims {articleClaimsExists ? "✓" : "✗"}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-1">Framework Relationship Tables:</h4>
              <ul className="list-disc ml-5">
                <li className={schemaInfo.tables.includes('article_pillar_impacts') ? "text-green-600" : "text-red-600"}>
                  article_pillar_impacts {schemaInfo.tables.includes('article_pillar_impacts') ? "✓" : "✗"}
                </li>
                <li className={schemaInfo.tables.includes('article_tactic_usage') ? "text-green-600" : "text-red-600"}>
                  article_tactic_usage {schemaInfo.tables.includes('article_tactic_usage') ? "✓" : "✗"}
                </li>
                <li className={schemaInfo.tables.includes('article_precedent_relevance') ? "text-green-600" : "text-red-600"}>
                  article_precedent_relevance {schemaInfo.tables.includes('article_precedent_relevance') ? "✓" : "✗"}
                </li>
                <li className={schemaInfo.tables.includes('article_rhetoric_markers') ? "text-green-600" : "text-red-600"}>
                  article_rhetoric_markers {schemaInfo.tables.includes('article_rhetoric_markers') ? "✓" : "✗"}
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <p className="text-red-600">Failed to load schema information</p>
        )}
        
        <button 
          onClick={fetchSchemaInfo}
          className="mt-3 text-blue-600 hover:text-blue-800 text-sm flex items-center"
        >
          <RefreshCw size={14} className="mr-1" />
          Refresh Schema Info
        </button>
      </div>

      {result ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
          <div className="flex items-start">
            <Check className="mt-0.5 mr-2 text-green-500" size={18} />
            <div>
              <p className="font-medium text-green-800">Schema update completed</p>
              <div className="mt-2 text-sm text-green-700">
                <p><strong>Tables created:</strong> {result.results?.created_tables?.length > 0 ? 
                  result.results.created_tables.join(', ') : 'None needed'}</p>
                
                <p><strong>Tables already existing:</strong> {result.results?.already_exists?.length > 0 ? 
                  result.results.already_exists.join(', ') : 'None'}</p>
                
                {result.results?.updated_tables && result.results.updated_tables.length > 0 && (
                  <p><strong>Tables updated:</strong> {result.results.updated_tables.join(', ')}</p>
                )}
                
                {result.results?.errors && result.results.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-yellow-600"><strong>Warnings:</strong></p>
                    <ul className="list-disc ml-5">
                      {result.results.errors.map((err, index) => (
                        <li key={index} className="text-yellow-600">
                          {err.table}: {err.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <div className="flex items-start">
            <X className="mt-0.5 mr-2 text-red-500" size={18} />
            <div>
              <p className="font-medium text-red-800">Schema update failed</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <div className="flex items-start">
          <AlertTriangle className="mt-0.5 mr-2 text-blue-600" size={18} />
          <div>
            <p className="font-medium text-blue-800">These actions are safe to run anytime</p>
            <p className="text-sm text-blue-700 mt-1">
              These operations will check for the existence of all necessary tables for article analysis and create
              any that are missing. They won't delete or modify any existing data. You can run these
              whenever you encounter errors about missing tables during article analysis.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-md p-4">
          <h3 className="font-medium mb-2">Core Article Tables</h3>
          <p className="text-sm text-gray-600 mb-3">
            Creates the base tables for article storage and analysis: 
            analyzed_articles, article_content, article_claims, etc.
          </p>
          <button
            onClick={() => handleCreateTables('/api/admin/create-article-tables')}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white font-medium flex items-center ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin mr-2" size={16} />
                Creating...
              </>
            ) : (
              <>
                <PlusCircle className="mr-2" size={16} />
                Create Core Tables
              </>
            )}
          </button>
        </div>
        
        <div className="border border-gray-200 rounded-md p-4">
          <h3 className="font-medium mb-2">Framework Relationship Tables</h3>
          <p className="text-sm text-gray-600 mb-3">
            Creates tables that connect articles to framework elements: 
            pillars, tactics, precedents, rhetoric markers, etc.
          </p>
          <button
            onClick={() => handleCreateTables('/api/admin/create-article-relationship-tables')}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white font-medium flex items-center ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin mr-2" size={16} />
                Creating...
              </>
            ) : (
              <>
                <PlusCircle className="mr-2" size={16} />
                Create Relationship Tables
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleSchemaManager;